import { useLayoutEffect, useRef, type FormEvent, type ReactNode } from "react";
import { Loader2, Send } from "lucide-react";
import { MAX_MESSAGE_LENGTH } from "../lib/api";
import { useI18n } from "../lib/i18n";

export function MessageComposer({ value, busy, onChange, onSubmit, autoFocus = false, maxLength = MAX_MESSAGE_LENGTH, attachment }: {
  value: string; busy: boolean; onChange: (value: string) => void; onSubmit: (event: FormEvent) => void; autoFocus?: boolean; maxLength?: number;
  attachment?: { action: ReactNode; preview: ReactNode; ready: boolean };
}) {
  const { t } = useI18n();
  const inputRef = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    // Bound draft growth so long text scrolls inside the input and leaves the
    // conversation's fixed composer usable on short mobile viewports.
    input.style.height = "auto";
    input.style.height = `${Math.min(input.scrollHeight + 2, 144)}px`;
  }, [value]);
  return <form onSubmit={onSubmit} className="shrink-0 border-t border-line bg-white px-4 pt-3" style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom, 0px))" }}>
    {attachment?.preview}
    <div className="flex items-end gap-3">
      {attachment?.action}
      <textarea ref={inputRef} aria-label={t("messages.typeMessage")} autoFocus={autoFocus} rows={1} maxLength={maxLength}
        value={value} onChange={(event) => onChange(event.target.value)} placeholder={t("messages.typeMessage")}
        onKeyDown={(event) => {
          if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
            event.preventDefault();
            if (!busy && (value.trim() || attachment?.ready) && value.length <= maxLength) event.currentTarget.form?.requestSubmit();
          }
        }}
        className="min-h-11 min-w-0 flex-1 resize-none overflow-y-auto rounded-xl border border-line bg-white px-4 py-3 text-sm leading-5 text-navy outline-none focus:border-navy-300" />
      <button type="submit" disabled={busy || (!value.trim() && !attachment?.ready) || value.length > maxLength} aria-label={t("messages.send")}
        className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-navy text-white transition-colors hover:bg-navy-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy focus-visible:ring-offset-2 disabled:opacity-40">
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
      </button>
    </div>
    {value.length > 0 && <p className="mt-1 px-4 text-right text-[10px] text-mut">{value.length}/{maxLength} · Shift + Enter for a new line</p>}
  </form>;
}
